/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import { Platform } from 'react-native';
import type {
  FirebaseWebConfig,
  RemoteConfigGetters,
} from './FlightConfigProvider';
import { defaultRemoteConfigValue } from '../utils/remoteConfig';

export type { RemoteConfigGetters } from './FlightConfigProvider';

const FIREBASE_REMOTE_CONFIG_FETCH_INTERVAL_MS = 3600000;

type RemoteConfigReader = {
  getBoolean: (key: string) => boolean;
  getString: (key: string) => string;
  getNumber: (key: string) => number;
};

const initAndFetch = async (
  firebaseConfig: FirebaseWebConfig,
  env?: string,
): Promise<RemoteConfigReader> => {
  const { initializeApp, getApp, getApps } = await import('firebase/app');
  const {
    getRemoteConfig,
    fetchAndActivate,
    getBoolean,
    getString,
    getNumber,
  } = await import('firebase/remote-config');

  const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
  const remoteConfig = getRemoteConfig(app);

  remoteConfig.settings.minimumFetchIntervalMillis =
    env !== 'prd' ? 0 : FIREBASE_REMOTE_CONFIG_FETCH_INTERVAL_MS;

  await fetchAndActivate(remoteConfig);

  return {
    getBoolean: (key: string) => getBoolean(remoteConfig, key),
    getString: (key: string) => getString(remoteConfig, key),
    getNumber: (key: string) => getNumber(remoteConfig, key),
  };
};

// Singleton: the app is initialized and Remote Config fetched/activated once
// per session, shared across every getter and key.
let readerPromise: Promise<RemoteConfigReader> | null = null;

const getReader = (
  firebaseConfig?: FirebaseWebConfig,
  env?: string,
): Promise<RemoteConfigReader | null> => {
  if (
    typeof window === 'undefined' ||
    Platform.OS !== 'web' ||
    !firebaseConfig
  ) {
    return Promise.resolve(null);
  }

  if (!readerPromise) {
    readerPromise = initAndFetch(firebaseConfig, env).catch((error) => {
      readerPromise = null; // reset so a later call can retry after a failure
      throw error;
    });
  }

  return readerPromise;
};

/**
 * Web Remote Config client. Connects to Firebase (once) using the host-provided
 * `firebaseConfig` and returns async typed getters. Each getter resolves to the
 * type default when the config is missing, on the server, or on error.
 */
export const createRemoteConfigClient = (
  firebaseConfig?: FirebaseWebConfig,
  env?: string,
): RemoteConfigGetters => {
  const read = <T>(
    getter: (reader: RemoteConfigReader) => T,
    fallback: T,
  ): Promise<T> =>
    getReader(firebaseConfig, env)
      .then((reader) => (reader ? getter(reader) : fallback))
      .catch(() => fallback);

  return {
    getRemoteConfigString: (key) =>
      read(
        (reader) => reader.getString(key),
        defaultRemoteConfigValue('string') as string,
      ),
    getRemoteConfigBoolean: (key) =>
      read(
        (reader) => reader.getBoolean(key),
        defaultRemoteConfigValue('boolean') as boolean,
      ),
    getRemoteConfigNumber: (key) =>
      read(
        (reader) => reader.getNumber(key),
        defaultRemoteConfigValue('number') as number,
      ),
  };
};
